/**
 * The `postMessage` contract between the applet iframe shell (server-owned —
 * see `api/applet-frame/`) and the host that embeds the frame (web and native
 * `AppletPreview`). This is the one piece of the applet preview that genuinely
 * crosses the server/web boundary, so it lives in `types/` as a single source
 * of truth both sides agree on.
 *
 * The frame runs at an opaque origin, so the host can't match on origin — it
 * matches `source === APPLET_MESSAGE_SOURCE` instead.
 */

import { z } from "zod";

import { APPLET_HOST_MESSAGE_SOURCE, APPLET_MESSAGE_SOURCE } from "./appletMessageSources.ts";

export { APPLET_HOST_MESSAGE_SOURCE, APPLET_MESSAGE_SOURCE };

/** The app has painted; the host can drop its loading spinner. */
export type AppletReadyMessage = {
  source: typeof APPLET_MESSAGE_SOURCE;
  type: "ready";
};

/** The frame reporting its own content height so the host can size the iframe
 * (the opaque origin means the host can't measure it directly). */
export type AppletResizeMessage = {
  source: typeof APPLET_MESSAGE_SOURCE;
  type: "resize";
  height: number;
};

/** A runtime error inside the applet. `kind` is a coarse classification:
 * `"network"` = an external fetch/resource failure the agent can't fix (the
 * host filters these out); `"app"` = a bug in the generated source the agent
 * can be asked to repair. `stack` is low-signal (it points at the in-browser
 * transpiled blob, not the app source) and is carried only for context. */
export type AppletErrorMessage = {
  source: typeof APPLET_MESSAGE_SOURCE;
  type: "error";
  message: string;
  stack?: string;
  kind: "app" | "network";
};

/** A whole-blob snapshot of the applet's `localStorage`, emitted by the shim on
 * every mutation. The host persists it (debounced) keyed by the applet, so the
 * store survives reloads. `blob` is string→string, mirroring Web Storage; the
 * host never trusts it for identity — it writes to the applet named by the
 * frame it's hosting. */
export type AppletStorageMessage = {
  source: typeof APPLET_MESSAGE_SOURCE;
  type: "storage";
  blob: Record<string, string>;
};

/**
 * The frame asking its host to call one of the applet-data routes for it,
 * which the opaque-origin frame can't do itself.
 *
 * The host makes one of two fixed calls on routes it derives from what it
 * framed: `ops` POSTs `body` to `…/data/ops`, and `refresh` GETs `…/data` with
 * `query`. Nothing in the message chooses a URL.
 */
export const appletDataRequestMessage = z.object({
  source: z.literal(APPLET_MESSAGE_SOURCE),
  type: z.literal("data.request"),
  requestId: z.string().min(1).max(64),
  op: z.enum(["ops", "refresh"]),
  query: z.record(z.string(), z.string()).optional(),
  body: z.unknown().optional(),
});
export type AppletDataRequestMessage = z.infer<typeof appletDataRequestMessage>;

export type AppletHostMessage =
  | AppletReadyMessage
  | AppletResizeMessage
  | AppletErrorMessage
  | AppletStorageMessage
  | AppletDataRequestMessage;

/**
 * The host's answer to a `data.request`: the HTTP status, or 0 when no response
 * arrived, and the parsed body.
 *
 * Passed through uninterpreted, so what they mean can change with the API
 * alone. Older native binaries keep older host code.
 */
export type AppletDataResponseMessage = {
  source: typeof APPLET_HOST_MESSAGE_SOURCE;
  type: "data.response";
  requestId: string;
  status: number;
  body: unknown;
};

/**
 * Query parameter a host adds to the frame URL when it relays `data.request`,
 * so the frame knows at load whether it can save.
 */
export const APPLET_PIPE_PARAM = "pipe";

/**
 * Query parameter carrying an anonymous link viewer's per-browser key, so the
 * applet can tell which records that viewer wrote. Display only: it authorizes
 * nothing.
 */
export const APPLET_WRITER_PARAM = "writer";
